/**
 * useListKeyboardNav — Tastatur-Navigation für Tabellen-Listen.
 *
 * Verwendung:
 *   const { selectedIndex, getRowProps } = useListKeyboardNav({
 *     items,
 *     onSelect: (item) => navigate(`/students/${item.id}`),
 *     enabled: !dialogOpen,
 *   });
 *
 *   <tr className={LIST_ROW_CLASS} {...getRowProps(idx, item)}>...</tr>
 *
 * Verhalten:
 *  • ArrowDown / ArrowUp  – wandert durch die Liste, scrollt automatisch
 *  • Home / End           – springt an Anfang/Ende
 *  • Enter                – ruft onSelect(item) auf
 *  • Klick auf die Zeile  – ruft onSelect(item) auf
 *  • Shortcut wird ignoriert, wenn der Fokus in einem Eingabefeld liegt
 *    (Input/Textarea/Select/contentEditable) oder enabled=false.
 *  • Hover über eine Zeile setzt zugleich die selectedIndex (kein "Springen").
 *  • Klicks auf nested button/a/input werden nicht als Zeilen-Select gewertet.
 */
import { useEffect, useRef, useState, useCallback } from 'react';

/** Einheitliche Zeilen-Styles für klickbare Listen. */
export const LIST_ROW_CLASS =
  'border-b border-slate-100 hover:bg-brand-red/5 transition-colors cursor-pointer data-[keyboard-selected=true]:bg-brand-red/10 data-[keyboard-selected=true]:ring-2 data-[keyboard-selected=true]:ring-brand-red/30 data-[keyboard-selected=true]:ring-inset';

const isEditable = (el) => {
  if (!el) return false;
  const tag = (el.tagName || '').toLowerCase();
  if (tag === 'input' || tag === 'textarea' || tag === 'select') return true;
  if (el.isContentEditable) return true;
  // shadcn Select Trigger uses role=combobox
  if (el.getAttribute && el.getAttribute('role') === 'combobox') return true;
  return false;
};

export default function useListKeyboardNav({ items, onSelect, enabled = true }) {
  const [selectedIndex, setSelectedIndex] = useState(-1);
  const rowRefs = useRef({});

  // Reset selection when items list changes (filter/search)
  useEffect(() => {
    setSelectedIndex(-1);
  }, [items?.length]);

  useEffect(() => {
    if (!enabled) return undefined;
    const handler = (e) => {
      if (isEditable(document.activeElement)) return;
      if (!items || items.length === 0) return;

      if (e.key === 'ArrowDown') {
        e.preventDefault();
        setSelectedIndex((i) => Math.min(items.length - 1, i + 1));
      } else if (e.key === 'ArrowUp') {
        e.preventDefault();
        setSelectedIndex((i) => Math.max(0, i - 1));
      } else if (e.key === 'Home') {
        e.preventDefault();
        setSelectedIndex(0);
      } else if (e.key === 'End') {
        e.preventDefault();
        setSelectedIndex(items.length - 1);
      } else if (e.key === 'Enter') {
        if (selectedIndex >= 0 && selectedIndex < items.length) {
          e.preventDefault();
          onSelect?.(items[selectedIndex]);
        }
      }
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [enabled, items, selectedIndex, onSelect]);

  // Auto-scroll the selected row into view
  useEffect(() => {
    if (selectedIndex < 0) return;
    const el = rowRefs.current[selectedIndex];
    if (el && el.scrollIntoView) {
      el.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
    }
  }, [selectedIndex]);

  const getRowProps = useCallback((idx, item) => ({
    ref: (el) => { rowRefs.current[idx] = el; },
    'data-keyboard-selected': idx === selectedIndex || undefined,
    onMouseEnter: () => setSelectedIndex(idx),
    onClick: (e) => {
      // Nested buttons/links handle their own action
      if (e.target.closest('button, a, input, select, textarea, [role="combobox"]')) return;
      onSelect?.(item);
    },
  }), [selectedIndex, onSelect]);

  return { selectedIndex, getRowProps, setSelectedIndex };
}
